/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Chat info — the two-column modal opened from the ChatRoomHeader avatar/name.
// Faithful to Figma "Desktop Chat Info" (POpUqpQSDHgRUerD8pFvCO node 6661:5171).
//
//   Sidebar (301px, #15171C): "Chat info" title + vertical tabs + subject footer.
//     1:1   → Profile · Media · Notifications · Self-Destruct · Security · Settings
//     group → Info · Media · Notifications · Self-Destruct · Security · Members · Settings
//   Main (#111318): tab title + ✕, then the active tab body:
//     Profile/Info — header (avatar + name + role + status + call/share) +
//       Voice/Video/Search/Polls + Favorite toggle + Saved messages + Pinned
//       messages + Threads + Notes (+ Members preview for groups).
//     Media        — Photos grid / Files list / Links list sub-tabs.
//     Notifications— Message-notifications radios + Sounds (sound + custom).
//     Self-Destruct— enable toggle + warning callout + Delete-after dropdown.
//     Security     — E2E status + encryption key (show/hide) + verify identity.
//     Members      — member list + per-member kebab + multi-select remove.
//     Settings     — nickname + safety actions (clear chat / block / report).
//
// Presentational: the host supplies data (mostly optional → graceful empty
// states) + action callbacks. Tailwind-first throughout.

import type React from 'react';
import { useRef, useState } from 'react';
import { Image, Modal as RNModal, Pressable, ScrollView, Text, TextInput, View } from 'react-native';
import { ShareProfileModal, type ShareProfileContact, type ShareResult } from './ShareProfileModal';
import {
  Ban,
  Bell,
  Bookmark,
  ChevronRight,
  Flag,
  Image as ImageIcon,
  Info,
  Lock,
  LogOut,
  Pencil,
  Pin,
  Search,
  Settings as SettingsIcon,
  Share2,
  ShieldAlert,
  Star,
  StickyNote,
  Timer,
  Trash2,
  UserPlus,
  UserRound,
  Users,
  Video,
  Volume2,
  X,
} from 'lucide-react-native';

import { Toggle } from '../settings-modal/controls';
import { ProfileAvatar } from '../profile/ProfileParts';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { AnchoredPopover } from '../components/AnchoredPopover';

const ICON_INACTIVE = '#B5BAC3';
const ICON_ACTIVE = '#F5F6F8';
const ICON_DANGER = '#FF5353';
const ACCENT = '#537EFF';

export type ChatInfoTab =
  | 'info'
  | 'media'
  | 'notifications'
  | 'selfDestruct'
  | 'security'
  | 'members'
  | 'settings';

export type ChatInfoNotificationLevel = 'default' | 'all' | 'mentions' | 'off';

export interface ChatInfoMessagePreview {
  readonly id: string;
  readonly senderName: string;
  readonly preview: string;
  readonly date?: string | undefined;
}

export interface ChatInfoThread {
  readonly id: string;
  readonly senderName: string;
  readonly preview: string;
  readonly date?: string | undefined;
  readonly replyCount?: number | undefined;
  readonly avatarUri?: string | undefined;
}

export interface ChatInfoNote {
  readonly id: string;
  readonly title: string;
  readonly preview?: string | undefined;
  readonly date?: string | undefined;
  readonly noteCount?: number | undefined;
}

export interface ChatInfoMediaFile {
  readonly id: string;
  readonly name: string;
  readonly meta: string;
  readonly dateGroup?: string | undefined;
}

export interface ChatInfoMediaLink {
  readonly id: string;
  readonly title: string;
  readonly url: string;
  readonly dateGroup?: string | undefined;
  readonly thumbUri?: string | undefined;
}

export interface ChatInfoMember {
  readonly id: string;
  readonly name: string;
  readonly role?: string | undefined;
  readonly subtitle?: string | undefined;
  readonly avatarUri?: string | undefined;
  readonly isAdmin?: boolean | undefined;
}

export interface ChatInfoStatus {
  readonly emoji?: string | undefined;
  readonly text: string;
  readonly until?: string | undefined;
}

export interface ChatInfoModalProps {
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly isGroup: boolean;
  /** True when the local user has LEFT this group. Every action that requires
   *  active membership (edit name/avatar, invite/share, add/remove members,
   *  make admin, calls, self-destruct) is then rendered disabled — dimmed
   *  (opacity) AND non-interactive (pointer events off). History/read-only
   *  surfaces (media, saved, notifications, favorite, delete) stay usable. */
  readonly hasLeftGroup?: boolean | undefined;
  readonly title: string;
  readonly subtitle?: string | undefined;
  readonly role?: string | undefined;
  readonly avatarUri?: string | undefined;
  readonly verified?: boolean | undefined;
  readonly status?: ChatInfoStatus | undefined;

  // Profile/Info
  readonly favorite?: boolean | undefined;
  readonly onToggleFavorite?: ((next: boolean) => void) | undefined;
  /** Open the image picker to set/replace the group avatar. When provided and
   *  `isGroup`, the header avatar becomes tappable. */
  readonly onPickAvatar?: (() => void) | undefined;
  /** Rename the group. When provided and `isGroup`, the header title becomes
   *  editable (tap the pencil → inline TextInput → save on blur/submit). */
  readonly onEditGroupName?: ((next: string) => void) | undefined;
  readonly savedMessages?: ReadonlyArray<ChatInfoMessagePreview> | undefined;
  readonly pinnedMessages?: ReadonlyArray<ChatInfoMessagePreview> | undefined;
  readonly threads?: ReadonlyArray<ChatInfoThread> | undefined;
  readonly notes?: ReadonlyArray<ChatInfoNote> | undefined;
  readonly onVoiceCall?: (() => void) | undefined;
  readonly onVideoCall?: (() => void) | undefined;
  readonly onSearch?: (() => void) | undefined;
  readonly onPolls?: (() => void) | undefined;
  /** Share via the native share sheet. */
  readonly onShare?: (() => void) | undefined;
  /** Copy the conversation/peer profile link to the clipboard (Share modal). */
  readonly onCopyLink?: (() => void) | undefined;
  /** Contacts the profile can be shared to (Share-profile modal multi-select). */
  readonly shareContacts?: ReadonlyArray<ShareProfileContact> | undefined;
  /** Share the profile to the selected contacts (Share-profile modal "Done").
   *  Receives the full selected contact objects so the host can resolve
   *  directory-searched users with no pre-existing conversation. */
  readonly onShareToContacts?:
    | ((contacts: readonly ShareProfileContact[]) => void | Promise<ShareResult>)
    | undefined;
  /** Directory search for the Share-profile modal — lets any Ping user (not just
   *  existing chats) be searched and shared to / invited to a group. */
  readonly onShareSearchUsers?:
    | ((query: string, signal: AbortSignal) => Promise<readonly ShareProfileContact[]>)
    | undefined;

  // Media
  readonly photos?: ReadonlyArray<{ readonly id: string; readonly uri: string }> | undefined;
  readonly files?: ReadonlyArray<ChatInfoMediaFile> | undefined;
  readonly links?: ReadonlyArray<ChatInfoMediaLink> | undefined;

  // Notifications
  readonly notificationLevel?: ChatInfoNotificationLevel | undefined;
  readonly onNotificationLevelChange?: ((level: ChatInfoNotificationLevel) => void) | undefined;
  readonly notificationSound?: string | undefined;
  readonly onPreviewSound?: (() => void) | undefined;
  readonly onResetSound?: (() => void) | undefined;
  readonly onBrowseSound?: (() => void) | undefined;
  readonly onSaveSound?: (() => void) | undefined;
  /** True once a custom sound has been picked but not yet saved — enables Save. */
  readonly soundDirty?: boolean | undefined;

  // Self-Destruct
  readonly selfDestructEnabled?: boolean | undefined;
  readonly onToggleSelfDestruct?: ((next: boolean) => void) | undefined;
  readonly selfDestructDuration?: string | undefined;
  readonly onPickSelfDestructDuration?: (() => void) | undefined;

  // Security
  readonly encryptionKey?: string | undefined;
  readonly onVerifyIdentity?: (() => void) | undefined;

  // Members (group)
  readonly members?: ReadonlyArray<ChatInfoMember> | undefined;
  readonly canManageMembers?: boolean | undefined;
  readonly onViewMemberProfile?: ((memberId: string) => void) | undefined;
  readonly onAddMemberContact?: ((memberId: string) => void) | undefined;
  readonly onMakeAdmin?: ((memberId: string) => void) | undefined;
  readonly onRemoveMembers?: ((memberIds: readonly string[]) => void) | undefined;
  /** Open the "add members to group" flow (search + multi-select). When set
   *  (and `canManageMembers`), an "Add member" affordance shows in the Members
   *  section header + atop the Members tab. */
  readonly onAddMembers?: (() => void) | undefined;

  // Settings
  readonly nickname?: string | undefined;
  readonly onSaveNickname?: ((next: string) => void) | undefined;
  readonly onClearChat?: (() => void) | undefined;
  readonly onBlockUser?: (() => void) | undefined;
  readonly onReportUser?: (() => void) | undefined;
  /** Leave (group) / delete (1:1) this conversation. Renders a destructive
   *  action in Settings; the host soft-deletes the local row + navigates away. */
  readonly onLeaveOrDelete?: (() => void) | undefined;
}

const TAB_META: Record<ChatInfoTab, { label: string; Icon: typeof Info }> = {
  info: { label: 'Profile', Icon: UserRound },
  media: { label: 'Media', Icon: ImageIcon },
  notifications: { label: 'Notifications', Icon: Bell },
  selfDestruct: { label: 'Self- Destruct', Icon: Timer },
  security: { label: 'Security', Icon: Lock },
  members: { label: 'Members', Icon: Users },
  settings: { label: 'Settings', Icon: SettingsIcon },
};

export function ChatInfoModal(props: ChatInfoModalProps): JSX.Element {
  const { visible, onClose, isGroup, title, role, avatarUri } = props;
  const tabs: readonly ChatInfoTab[] = isGroup
    ? ['info', 'media', 'notifications', 'selfDestruct', 'security', 'members', 'settings']
    : ['info', 'media', 'notifications', 'selfDestruct', 'security', 'settings'];
  const [active, setActive] = useState<ChatInfoTab>('info');
  const headerLabel = active === 'info' && isGroup ? 'Info' : TAB_META[active].label;

  return (
    <RNModal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
      accessibilityViewIsModal
      accessibilityLabel="Chat info"
    >
      {/* Backdrop click-catcher. Intentionally NOT a button (no
          accessibilityRole) so it renders a <div>, not a <button> — otherwise
          the action buttons inside nest <button> in <button> (invalid DOM). The
          accessible close affordance is the explicit X button in the header. */}
      <Pressable
        className="flex-1 items-center justify-center bg-black/50 p-4"
        onPress={onClose}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          className="w-full max-w-[1080px] h-full max-h-[704px] flex-row overflow-hidden rounded-2xl"
        >
          {/* Sidebar */}
          <View className="w-[301px] bg-light-surface-panel dark:bg-dark-surface-panel">
            <Text className="px-6 pt-5 pb-3 text-[24px] font-bold text-light-text-primary dark:text-dark-text-primary">
              Chat info
            </Text>
            <View className="flex-1 gap-0.5 px-3">
              {tabs.map((tab) => {
                const { label, Icon } = TAB_META[tab];
                const isActive = tab === active;
                return (
                  <Pressable
                    key={tab}
                    accessibilityRole="tab"
                    accessibilityState={{ selected: isActive }}
                    onPress={() => setActive(tab)}
                    className={`h-11 flex-row items-center gap-3 rounded-lg px-3 ${
                      isActive
                        ? 'bg-light-surface-elevated dark:bg-dark-surface-elevated'
                        : 'active:bg-light-surface-elevated dark:active:bg-dark-surface-elevated'
                    }`}
                  >
                    <Icon size={18} color={isActive ? ICON_ACTIVE : ICON_INACTIVE} />
                    <Text
                      className={`flex-1 text-[16px] ${
                        isActive
                          ? 'text-light-text-primary dark:text-dark-text-primary font-semibold'
                          : 'text-light-text-secondary dark:text-dark-text-secondary'
                      }`}
                    >
                      {label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
            {/* Subject footer */}
            <View className="border-t border-light-border-subtle dark:border-dark-border-subtle px-4 py-3 flex-row items-center gap-3">
              <ProfileAvatar name={title} uri={avatarUri} sizeClassName="w-9 h-9 rounded-full" />
              <View className="flex-1 min-w-0">
                <Text
                  className="text-[14px] font-bold text-light-text-primary dark:text-dark-text-primary"
                  numberOfLines={1}
                >
                  {title}
                </Text>
                {role !== undefined && role.length > 0 ? (
                  <Text
                    className="text-[12px] text-light-text-tertiary dark:text-dark-text-tertiary"
                    numberOfLines={1}
                  >
                    {role}
                  </Text>
                ) : null}
              </View>
            </View>
          </View>

          {/* Main */}
          <View className="flex-1 min-w-0 bg-light-surface-app dark:bg-[#111318]">
            <View className="h-[58px] flex-row items-center justify-between px-4">
              <Text className="text-[24px] font-bold text-light-text-secondary dark:text-dark-text-secondary">
                {headerLabel}
              </Text>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Close"
                onPress={onClose}
                hitSlop={8}
                className="h-7 w-7 items-center justify-center rounded-full active:opacity-70"
              >
                <X size={20} color={ICON_ACTIVE} />
              </Pressable>
            </View>

            {active === 'info' ? (
              <InfoTab {...props} onOpenMembers={() => setActive('members')} />
            ) : active === 'media' ? (
              <MediaTab {...props} />
            ) : active === 'notifications' ? (
              <NotificationsTab {...props} />
            ) : active === 'selfDestruct' ? (
              <SelfDestructTab {...props} />
            ) : active === 'security' ? (
              <SecurityTab {...props} />
            ) : active === 'members' ? (
              <MembersTab {...props} />
            ) : (
              <SettingsTab {...props} />
            )}
          </View>
        </Pressable>
      </Pressable>
    </RNModal>
  );
}

// ── Shared bits ──────────────────────────────────────────────────────────

/** Two-column settings row: 180px label column + flexible content. */
function InfoFieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <View className="flex-row gap-4">
      <Text className="w-[160px] shrink-0 text-[16px] font-bold text-light-text-primary dark:text-dark-text-primary">
        {label}
      </Text>
      <View className="flex-1">{children}</View>
    </View>
  );
}

function SectionHeader({
  label,
  onViewAll,
  onAdd,
  addLabel = 'Add member',
  addLocked = false,
}: {
  label: string;
  onViewAll?: (() => void) | undefined;
  /** Optional trailing action rendered after "View all" (e.g. add member). */
  onAdd?: (() => void) | undefined;
  addLabel?: string;
  /** When true, the "add" action is shown but dimmed + non-interactive (the
   *  local user has left the group and can't add members). */
  addLocked?: boolean;
}): JSX.Element {
  return (
    <View className="flex-row items-center justify-between">
      <Text className="text-[12px] text-light-text-tertiary dark:text-dark-text-tertiary">
        {label}
      </Text>
      <View className="flex-row items-center gap-4">
        {onViewAll !== undefined ? (
          <Pressable accessibilityRole="button" onPress={onViewAll} className="active:opacity-70">
            <Text className="text-[12px] text-light-accent-brand dark:text-dark-accent-brand">
              View all
            </Text>
          </Pressable>
        ) : null}
        {onAdd !== undefined ? (
          <Gated locked={addLocked}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={addLabel}
              disabled={addLocked}
              onPress={onAdd}
              className="flex-row items-center gap-1 active:opacity-70"
            >
              <UserPlus size={12} color={ACCENT} />
              <Text className="text-[12px] text-light-accent-brand dark:text-dark-accent-brand">
                {addLabel}
              </Text>
            </Pressable>
          </Gated>
        ) : null}
      </View>
    </View>
  );
}

function EmptyHint({ text }: { text: string }): JSX.Element {
  return (
    <Text className="text-[13px] text-light-text-tertiary dark:text-dark-text-tertiary">{text}</Text>
  );
}

// ── Info / Profile tab ───────────────────────────────────────────────────

/** Wraps a membership-requiring control so that, once the local user has left
 *  the group, it's rendered DIMMED (opacity) and NON-INTERACTIVE (pointer events
 *  off). `View pointerEvents="none"` blocks taps on both web and native, and the
 *  opacity communicates the disabled state visually. When not locked it's a
 *  transparent pass-through (no layout/opacity change). */
function Gated({
  locked,
  className,
  children,
}: {
  locked: boolean;
  className?: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <View
      pointerEvents={locked ? 'none' : 'auto'}
      style={locked ? { opacity: 0.4 } : undefined}
      {...(className !== undefined ? { className } : {})}
    >
      {children}
    </View>
  );
}

function InfoActionButton({
  Icon,
  label,
  onPress,
  disabled,
}: {
  Icon: typeof Info;
  label: string;
  onPress?: (() => void) | undefined;
  disabled?: boolean | undefined;
}): JSX.Element {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled: disabled === true }}
      disabled={disabled === true}
      pointerEvents={disabled === true ? 'none' : 'auto'}
      onPress={onPress}
      className={`h-10 flex-1 flex-row items-center justify-center gap-2 rounded-lg bg-light-surface-panel dark:bg-dark-surface-panel active:opacity-80 ${
        disabled === true ? 'opacity-40' : ''
      }`}
    >
      <Icon size={20} color={ICON_ACTIVE} />
      <Text className="text-[12px] text-light-text-secondary dark:text-dark-text-secondary">
        {label}
      </Text>
    </Pressable>
  );
}

function MessageRow({
  item,
  pinned,
}: {
  item: ChatInfoMessagePreview;
  pinned?: boolean;
}): JSX.Element {
  return (
    <View className="flex-row items-start gap-2 py-1.5">
      <View className="mt-0.5">
        {pinned === true ? (
          <Pin size={14} color="#8A8F98" />
        ) : (
          <Bookmark size={14} color="#8A8F98" />
        )}
      </View>
      <View className="flex-1 min-w-0">
        <Text
          className="text-[14px] font-semibold text-light-text-primary dark:text-dark-text-primary"
          numberOfLines={1}
        >
          {item.senderName}
        </Text>
        <Text
          className="text-[13px] text-light-text-tertiary dark:text-dark-text-tertiary"
          numberOfLines={1}
        >
          {item.preview}
        </Text>
      </View>
      {item.date !== undefined ? (
        <Text className="text-[12px] text-light-text-tertiary dark:text-dark-text-tertiary">
          {item.date}
        </Text>
      ) : null}
    </View>
  );
}

function InfoTab(props: ChatInfoModalProps & { onOpenMembers: () => void }): JSX.Element {
  const {
    title,
    role,
    avatarUri,
    verified,
    status,
    favorite,
    onToggleFavorite,
    onPickAvatar,
    onEditGroupName,
    savedMessages,
    pinnedMessages,
    threads,
    notes,
    isGroup,
    members,
    onOpenMembers,
    canManageMembers,
    onAddMembers,
  } = props;
  const [fav, setFav] = useState(favorite ?? false);
  // LEFT-GROUP LOCK: once the local user has left the group, every action that
  // requires active membership is rendered dimmed + non-interactive (see the
  // `Gated` wrapper). Read-only / per-device surfaces stay usable.
  const leftLock = isGroup && props.hasLeftGroup === true;
  // Group rename: inline-edit state for the header title. `editingName` holds
  // the draft while open; commit on submit/blur via `onEditGroupName`.
  const [editingName, setEditingName] = useState<string | null>(null);
  const canEditName = isGroup && onEditGroupName !== undefined;
  const commitName = (): void => {
    if (editingName === null) return;
    const next = editingName.trim();
    if (next.length > 0 && next !== title) onEditGroupName?.(next);
    setEditingName(null);
  };
  // "Share profile" popover (Figma 6661:6518) — anchored below the Share button
  // via AnchoredPopover, which measures the ref on open (first-click reliable).
  const [shareOpen, setShareOpen] = useState(false);
  const shareBtnRef = useRef<unknown>(null);
  const saved = savedMessages ?? [];
  const pinned = pinnedMessages ?? [];
  const threadList = threads ?? [];
  const noteList = notes ?? [];
  const memberPreview = (members ?? []).slice(0, 5);

  return (
    <ScrollView contentContainerStyle={{ padding: 16, gap: 16 }} showsVerticalScrollIndicator={false}>
      {/* Header */}
      <View className="flex-row items-center gap-3">
        {isGroup && onPickAvatar !== undefined && !leftLock ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Change group photo"
            onPress={onPickAvatar}
          >
            <ProfileAvatar name={title} uri={avatarUri} sizeClassName="w-[52px] h-[52px] rounded-2xl" />
          </Pressable>
        ) : (
          <ProfileAvatar name={title} uri={avatarUri} sizeClassName="w-[52px] h-[52px] rounded-2xl" />
        )}
        <View className="flex-1 min-w-0">
          <View className="flex-row items-center gap-1.5">
            {editingName !== null ? (
              <TextInput
                value={editingName}
                onChangeText={setEditingName}
                onSubmitEditing={commitName}
                onBlur={commitName}
                autoFocus
                placeholder="Group name"
                className="flex-1 text-[16px] font-bold text-light-text-primary dark:text-dark-text-primary border-b border-light-border-subtle dark:border-dark-border-subtle"
              />
            ) : (
              <Text
                className="text-[16px] font-bold text-light-text-primary dark:text-dark-text-primary"
                numberOfLines={1}
              >
                {title}
              </Text>
            )}
            {verified === true ? <VerifiedBadge verified size={16} /> : null}
            {canEditName && editingName === null ? (
              <Gated locked={leftLock}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Rename group"
                  hitSlop={8}
                  onPress={() => setEditingName(title)}
                >
                  <Pencil size={15} color="#8A8F98" />
                </Pressable>
              </Gated>
            ) : null}
          </View>
          {role !== undefined && role.length > 0 ? (
            <Text
              className="text-[14px] text-light-text-secondary dark:text-dark-text-secondary"
              numberOfLines={1}
            >
              {role}
            </Text>
          ) : null}
          {status !== undefined && status.text.length > 0 ? (
            <View className="mt-0.5 flex-row items-center gap-1.5">
              {status.emoji !== undefined ? <Text className="text-[13px]">{status.emoji}</Text> : null}
              <Text className="text-[13px] font-semibold text-light-text-primary dark:text-dark-text-primary">
                {status.text}
              </Text>
              {status.until !== undefined ? (
                <Text className="text-[13px] text-light-text-tertiary dark:text-dark-text-tertiary">
                  {status.until}
                </Text>
              ) : null}
            </View>
          ) : null}
        </View>
        <Gated locked={leftLock}>
          <Pressable
            ref={shareBtnRef as never}
            accessibilityRole="button"
            accessibilityLabel="Share"
            disabled={leftLock}
            onPress={() => setShareOpen((o) => !o)}
            className="h-8 w-8 items-center justify-center rounded-full bg-light-surface-elevated dark:bg-dark-surface-elevated active:opacity-80"
          >
            <Share2 size={16} color={ICON_ACTIVE} />
          </Pressable>
        </Gated>
        <ShareProfileModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          anchorRef={shareBtnRef}
          contacts={props.shareContacts ?? []}
          {...(props.onCopyLink !== undefined ? { onCopyLink: props.onCopyLink } : {})}
          {...(props.onShareToContacts !== undefined ? { onShare: props.onShareToContacts } : {})}
          {...(props.onShareSearchUsers !== undefined ? { onSearchUsers: props.onShareSearchUsers } : {})}
        />
      </View>

      {/* Actions — Voice/Video/Polls need active membership; Search reads local
          history so it stays available after leaving. */}
      <View className="flex-row gap-2">
        <InfoActionButton Icon={UserRound} label="Voice call" onPress={props.onVoiceCall} disabled={leftLock} />
        <InfoActionButton Icon={Video} label="Video call" onPress={props.onVideoCall} disabled={leftLock} />
        <InfoActionButton Icon={Search} label="Search" onPress={props.onSearch} />
        <InfoActionButton Icon={Info} label="Polls" onPress={props.onPolls} disabled={leftLock} />
      </View>

      {/* Favorite */}
      <View className="flex-row items-center justify-between">
        <View className="flex-row items-center gap-2">
          <Star size={18} color={ACCENT} fill={ACCENT} />
          <Text className="text-[15px] text-light-text-primary dark:text-dark-text-primary">
            Favorite
          </Text>
        </View>
        <Toggle
          checked={fav}
          onChange={(next) => {
            setFav(next);
            onToggleFavorite?.(next);
          }}
          label="Favorite"
        />
      </View>

      {/* Members preview (group) */}
      {isGroup && memberPreview.length > 0 ? (
        <View className="gap-1.5">
          <SectionHeader
            label="Members"
            onViewAll={onOpenMembers}
            addLocked={leftLock}
            {...(canManageMembers === true && onAddMembers !== undefined
              ? { onAdd: onAddMembers }
              : {})}
          />
          {memberPreview.map((m) => (
            <MemberRow key={m.id} member={m} {...props} compact />
          ))}
        </View>
      ) : null}

      {/* Saved messages */}
      <View className="gap-1.5">
        <SectionHeader label="Saved messages" />
        {saved.length > 0 ? (
          saved.map((m) => <MessageRow key={m.id} item={m} />)
        ) : (
          <EmptyHint text="No saved messages yet." />
        )}
      </View>

      {/* Pinned messages */}
      <View className="gap-1.5">
        <SectionHeader label="Pinned messages" />
        {pinned.length > 0 ? (
          pinned.map((m) => <MessageRow key={m.id} item={m} pinned />)
        ) : (
          <EmptyHint text="No pinned messages yet." />
        )}
      </View>

      {/* Threads */}
      {threadList.length > 0 ? (
        <View className="gap-1.5">
          <SectionHeader label="Threads" />
          {threadList.map((t) => (
            <View key={t.id} className="flex-row items-start gap-2 py-1.5">
              <ProfileAvatar name={t.senderName} uri={t.avatarUri} sizeClassName="w-7 h-7 rounded-full" />
              <View className="flex-1 min-w-0">
                <Text
                  className="text-[14px] font-semibold text-light-text-primary dark:text-dark-text-primary"
                  numberOfLines={1}
                >
                  {t.senderName}
                </Text>
                <Text
                  className="text-[13px] text-light-text-tertiary dark:text-dark-text-tertiary"
                  numberOfLines={1}
                >
                  {t.preview}
                </Text>
                {t.replyCount !== undefined ? (
                  <Text className="mt-0.5 text-[12px] text-light-accent-brand dark:text-dark-accent-brand">
                    {t.replyCount} replies
                  </Text>
                ) : null}
              </View>
              {t.date !== undefined ? (
                <Text className="text-[12px] text-light-text-tertiary dark:text-dark-text-tertiary">
                  {t.date}
                </Text>
              ) : null}
            </View>
          ))}
        </View>
      ) : null}

      {/* Notes */}
      {noteList.length > 0 ? (
        <View className="gap-1.5">
          <SectionHeader label="Notes" />
          {noteList.map((n) => (
            <View key={n.id} className="flex-row items-start gap-2 py-1.5">
              <View className="mt-0.5">
                <StickyNote size={14} color="#8A8F98" />
              </View>
              <View className="flex-1 min-w-0">
                <Text
                  className="text-[14px] font-semibold text-light-text-primary dark:text-dark-text-primary"
                  numberOfLines={1}
                >
                  {n.title}
                </Text>
                {n.preview !== undefined ? (
                  <Text
                    className="text-[13px] text-light-text-tertiary dark:text-dark-text-tertiary"
                    numberOfLines={1}
                  >
                    {n.preview}
                  </Text>
                ) : null}
                {n.noteCount !== undefined ? (
                  <Text className="mt-0.5 text-[12px] text-light-text-tertiary dark:text-dark-text-tertiary">
                    {n.noteCount} notes
                  </Text>
                ) : null}
              </View>
              {n.date !== undefined ? (
                <Text className="text-[12px] text-light-text-tertiary dark:text-dark-text-tertiary">
                  {n.date}
                </Text>
              ) : null}
            </View>
          ))}
        </View>
      ) : null}
    </ScrollView>
  );
}

// ── Media tab ────────────────────────────────────────────────────────────

function MediaTab(props: ChatInfoModalProps): JSX.Element {
  const { photos, files, links } = props;
  const [sub, setSub] = useState<'photos' | 'files' | 'links'>('photos');
  const subTabs: ReadonlyArray<{ key: typeof sub; label: string }> = [
    { key: 'photos', label: 'Photos' },
    { key: 'files', label: 'Files' },
    { key: 'links', label: 'Links' },
  ];

  return (
    <View className="flex-1">
      {/* Sub-tabs */}
      <View className="flex-row gap-6 px-4 border-b border-light-border-subtle dark:border-dark-border-subtle">
        {subTabs.map((t) => {
          const isActive = t.key === sub;
          return (
            <Pressable
              key={t.key}
              accessibilityRole="tab"
              onPress={() => setSub(t.key)}
              className={`pb-2 ${isActive ? 'border-b-2 border-light-accent-brand dark:border-dark-accent-brand' : ''}`}
            >
              <Text
                className={`text-[14px] ${
                  isActive
                    ? 'text-light-text-primary dark:text-dark-text-primary font-semibold'
                    : 'text-light-text-secondary dark:text-dark-text-secondary'
                }`}
              >
                {t.label}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <ScrollView contentContainerStyle={{ padding: 16 }} showsVerticalScrollIndicator={false}>
        {sub === 'photos' ? (
          (photos ?? []).length > 0 ? (
            <View className="flex-row flex-wrap gap-1.5">
              {(photos ?? []).map((p) => (
                <Image
                  key={p.id}
                  source={{ uri: p.uri }}
                  className="w-[23%] aspect-square rounded-md"
                  resizeMode="cover"
                  accessibilityLabel=""
                />
              ))}
            </View>
          ) : (
            <EmptyHint text="No photos shared yet." />
          )
        ) : sub === 'files' ? (
          (files ?? []).length > 0 ? (
            <View className="gap-2">
              {(files ?? []).map((f) => (
                <View key={f.id} className="flex-row items-center gap-3 py-1">
                  <View className="h-9 w-9 items-center justify-center rounded-md bg-light-surface-elevated dark:bg-dark-surface-elevated">
                    <ImageIcon size={18} color={ICON_INACTIVE} />
                  </View>
                  <View className="flex-1 min-w-0">
                    <Text
                      className="text-[14px] text-light-text-primary dark:text-dark-text-primary"
                      numberOfLines={1}
                    >
                      {f.name}
                    </Text>
                    <Text className="text-[12px] text-light-text-tertiary dark:text-dark-text-tertiary">
                      {f.meta}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          ) : (
            <EmptyHint text="No files shared yet." />
          )
        ) : (links ?? []).length > 0 ? (
          <View className="gap-2">
            {(links ?? []).map((l) => (
              <View key={l.id} className="flex-row items-center gap-3 py-1">
                <View className="h-9 w-9 items-center justify-center rounded-md bg-light-surface-elevated dark:bg-dark-surface-elevated">
                  <Share2 size={16} color={ICON_INACTIVE} />
                </View>
                <View className="flex-1 min-w-0">
                  <Text
                    className="text-[14px] text-light-text-primary dark:text-dark-text-primary"
                    numberOfLines={1}
                  >
                    {l.title}
                  </Text>
                  <Text
                    className="text-[12px] text-light-text-tertiary dark:text-dark-text-tertiary"
                    numberOfLines={1}
                  >
                    {l.url}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        ) : (
          <EmptyHint text="No links shared yet." />
        )}
      </ScrollView>
    </View>
  );
}

// ── Notifications tab ────────────────────────────────────────────────────

function RadioOption({
  selected,
  title,
  description,
  onPress,
}: {
  selected: boolean;
  title: string;
  description: string;
  onPress: () => void;
}): JSX.Element {
  return (
    <Pressable
      accessibilityRole="radio"
      accessibilityState={{ selected }}
      onPress={onPress}
      className="flex-row gap-3 py-2 active:opacity-80"
    >
      <View
        className={`mt-0.5 h-5 w-5 items-center justify-center rounded-full border-2 ${
          selected
            ? 'border-light-accent-brand dark:border-dark-accent-brand'
            : 'border-light-border-subtle dark:border-dark-border-subtle'
        }`}
      >
        {selected ? <View className="h-2.5 w-2.5 rounded-full bg-light-accent-brand dark:bg-dark-accent-brand" /> : null}
      </View>
      <View className="flex-1">
        <Text className="text-[15px] text-light-text-primary dark:text-dark-text-primary">{title}</Text>
        <Text className="text-[13px] text-light-text-tertiary dark:text-dark-text-tertiary">
          {description}
        </Text>
      </View>
    </Pressable>
  );
}

function NotificationsTab(props: ChatInfoModalProps): JSX.Element {
  const { notificationLevel, onNotificationLevelChange, notificationSound } = props;
  const [level, setLevel] = useState<ChatInfoNotificationLevel>(notificationLevel ?? 'default');
  const options: ReadonlyArray<{ key: ChatInfoNotificationLevel; title: string; desc: string }> = [
    { key: 'default', title: 'Default', desc: 'Get notifications as set up in your settings' },
    { key: 'all', title: 'All messages', desc: 'Get notified for every message' },
    { key: 'mentions', title: '@mentions & keywords', desc: 'Get notified only with mentions and keywords' },
    { key: 'off', title: 'Off', desc: "You won't get any notifications" },
  ];

  return (
    <ScrollView contentContainerStyle={{ padding: 16, gap: 16 }} showsVerticalScrollIndicator={false}>
      <InfoFieldRow label="Message notifications">
        <View>
          {options.map((o) => (
            <RadioOption
              key={o.key}
              selected={level === o.key}
              title={o.title}
              description={o.desc}
              onPress={() => {
                setLevel(o.key);
                onNotificationLevelChange?.(o.key);
              }}
            />
          ))}
        </View>
      </InfoFieldRow>

      <View className="h-px bg-light-border-subtle dark:bg-dark-border-subtle" />

      <InfoFieldRow label="Sounds">
        <View className="gap-3">
          <Text className="text-[13px] text-light-text-secondary dark:text-dark-text-secondary">
            Notification sound
          </Text>
          <View className="flex-row items-center gap-3">
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Choose notification sound"
              onPress={props.onBrowseSound}
              className="h-9 flex-1 max-w-[320px] flex-row items-center justify-between rounded-lg bg-light-surface-elevated dark:bg-dark-surface-elevated px-3 active:opacity-80"
            >
              <Text className="text-[14px] text-light-text-primary dark:text-dark-text-primary">
                {notificationSound ?? 'Classic'}
              </Text>
              <ChevronRight size={16} color={ICON_INACTIVE} />
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Preview sound"
              onPress={props.onPreviewSound}
              className="h-9 w-9 items-center justify-center rounded-full bg-light-surface-elevated dark:bg-dark-surface-elevated active:opacity-80"
            >
              <Volume2 size={18} color={ICON_ACTIVE} />
            </Pressable>
            <Pressable accessibilityRole="button" onPress={props.onResetSound} className="active:opacity-70">
              <Text className="text-[14px] text-light-text-secondary dark:text-dark-text-secondary">
                Reset
              </Text>
            </Pressable>
          </View>
          <Text className="mt-1 text-[13px] text-light-text-secondary dark:text-dark-text-secondary">
            Set a new custom sound
          </Text>
          <View className="flex-row items-center gap-3">
            <Pressable
              accessibilityRole="button"
              onPress={props.onBrowseSound}
              className="h-9 items-center justify-center rounded-lg bg-light-accent-brand dark:bg-dark-accent-brand px-4 active:opacity-80"
            >
              <Text className="text-[14px] font-bold text-white">Browse</Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              onPress={props.soundDirty === true ? props.onSaveSound : undefined}
              disabled={props.soundDirty !== true}
              className={`active:opacity-70 ${props.soundDirty === true ? '' : 'opacity-40'}`}
            >
              <Text className="text-[14px] text-light-text-primary dark:text-dark-text-primary">Save</Text>
            </Pressable>
          </View>
        </View>
      </InfoFieldRow>
    </ScrollView>
  );
}

// ── Self-Destruct tab ────────────────────────────────────────────────────

function SelfDestructTab(props: ChatInfoModalProps): JSX.Element {
  const { selfDestructEnabled, onToggleSelfDestruct, selfDestructDuration } = props;
  const [on, setOn] = useState(selfDestructEnabled ?? false);

  return (
    <ScrollView contentContainerStyle={{ padding: 16, gap: 16 }} showsVerticalScrollIndicator={false}>
      <InfoFieldRow label="Self destruct messages">
        <View className="gap-3">
          <View className="flex-row items-center gap-3">
            <Toggle
              checked={on}
              onChange={(next) => {
                setOn(next);
                onToggleSelfDestruct?.(next);
              }}
              label="Self destruct messages"
            />
            <Text className="flex-1 text-[14px] text-light-text-secondary dark:text-dark-text-secondary">
              Auto-delete messages after the selected time
            </Text>
          </View>
          {on ? (
            <View className="flex-row gap-2 rounded-lg border border-light-accent-brand/55 dark:border-dark-accent-brand/55 bg-light-surface-elevated/40 dark:bg-dark-surface-elevated/40 px-3 py-2.5">
              <ShieldAlert size={16} color={ACCENT} />
              <Text className="flex-1 text-[13px] leading-[18px] text-light-text-secondary dark:text-dark-text-secondary">
                When enabled, all new messages in this chat will automatically delete after the
                selected time period. This cannot be undone.
              </Text>
            </View>
          ) : null}
        </View>
      </InfoFieldRow>

      {on ? (
        <>
          <View className="h-px bg-light-border-subtle dark:bg-dark-border-subtle" />
          <InfoFieldRow label="Delete after">
            <View className="gap-2">
              <Text className="text-[13px] text-light-text-secondary dark:text-dark-text-secondary">
                Set time
              </Text>
              <Pressable
                accessibilityRole="button"
                onPress={props.onPickSelfDestructDuration}
                className="h-9 max-w-[320px] flex-row items-center justify-between rounded-lg bg-light-surface-elevated dark:bg-dark-surface-elevated px-3 active:opacity-80"
              >
                <Text className="text-[14px] text-light-text-primary dark:text-dark-text-primary">
                  {selfDestructDuration ?? '5 seconds'}
                </Text>
                <ChevronRight size={16} color={ICON_INACTIVE} />
              </Pressable>
            </View>
          </InfoFieldRow>
        </>
      ) : null}
    </ScrollView>
  );
}

// ── Security tab ─────────────────────────────────────────────────────────

function SecurityTab(props: ChatInfoModalProps): JSX.Element {
  const { encryptionKey, title, onVerifyIdentity } = props;
  const [showKey, setShowKey] = useState(false);

  return (
    <ScrollView contentContainerStyle={{ padding: 16, gap: 16 }} showsVerticalScrollIndicator={false}>
      <InfoFieldRow label="Encryption">
        <View className="gap-4">
          <View>
            <Text className="text-[16px] text-light-text-primary dark:text-dark-text-primary">
              End-to-End Encrypted
            </Text>
            <Text className="text-[14px] text-light-text-tertiary dark:text-dark-text-tertiary">
              Messages are secured with E2E encryption
            </Text>
          </View>

          {encryptionKey !== undefined && encryptionKey.length > 0 ? (
            <View className="gap-2">
              <View className="flex-row items-center justify-between">
                <Text className="text-[16px] text-light-text-primary dark:text-dark-text-primary">
                  Encryption key
                </Text>
                <Pressable
                  accessibilityRole="button"
                  onPress={() => setShowKey((s) => !s)}
                  className="active:opacity-70"
                >
                  <Text className="text-[14px] text-light-text-secondary dark:text-dark-text-secondary">
                    {showKey ? 'Hide' : 'Show'}
                  </Text>
                </Pressable>
              </View>
              {showKey ? (
                <View className="rounded-lg bg-light-surface-elevated dark:bg-dark-surface-elevated p-4">
                  <Text className="font-mono text-[14px] leading-[28px] tracking-[2px] text-light-text-secondary dark:text-dark-text-secondary">
                    {encryptionKey}
                  </Text>
                </View>
              ) : null}
            </View>
          ) : null}

          <Pressable
            accessibilityRole="button"
            onPress={onVerifyIdentity}
            className="flex-row items-center justify-between active:opacity-70"
          >
            <Text className="text-[16px] text-light-text-primary dark:text-dark-text-primary">
              Verify {title.split(' ')[0]}'s identity
            </Text>
            <ChevronRight size={18} color={ICON_INACTIVE} />
          </Pressable>
        </View>
      </InfoFieldRow>
    </ScrollView>
  );
}

// ── Settings tab ─────────────────────────────────────────────────────────

function SafetyAction({
  Icon,
  label,
  onPress,
}: {
  Icon: typeof Info;
  label: string;
  onPress?: (() => void) | undefined;
}): JSX.Element {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      className="flex-row items-center gap-3 py-1.5 active:opacity-70"
    >
      <Icon size={18} color={ICON_DANGER} />
      <Text className="text-[15px] text-[#FF5353]">{label}</Text>
    </Pressable>
  );
}

function SettingsTab(props: ChatInfoModalProps): JSX.Element {
  const { nickname, onSaveNickname } = props;
  const [value, setValue] = useState(nickname ?? '');

  return (
    <ScrollView contentContainerStyle={{ padding: 16, gap: 16 }} showsVerticalScrollIndicator={false}>
      <InfoFieldRow label="General">
        <View className="gap-2">
          <Text className="text-[13px] text-light-text-secondary dark:text-dark-text-secondary">
            Nickname
          </Text>
          <View className="flex-row items-center gap-3">
            <TextInput
              value={value}
              onChangeText={setValue}
              onEndEditing={() => onSaveNickname?.(value.trim())}
              onSubmitEditing={() => onSaveNickname?.(value.trim())}
              placeholder="No nickname set"
              placeholderTextColor="#6B7280"
              accessibilityLabel="Nickname"
              maxLength={64}
              className="h-10 flex-1 max-w-[420px] rounded-lg border border-light-border-subtle dark:border-dark-border-subtle bg-light-surface-elevated dark:bg-dark-surface-elevated px-3 text-[15px] text-light-text-primary dark:text-dark-text-primary"
            />
            <Pressable
              accessibilityRole="button"
              onPress={() => {
                setValue('');
                onSaveNickname?.('');
              }}
              className="active:opacity-70"
            >
              <Text className="text-[14px] text-light-text-tertiary dark:text-dark-text-tertiary">
                Reset
              </Text>
            </Pressable>
          </View>
          <Text className="text-[13px] text-light-text-tertiary dark:text-dark-text-tertiary">
            Only visible to you. Helpful if you know them by a different name.
          </Text>
        </View>
      </InfoFieldRow>

      <View className="h-px bg-light-border-subtle dark:bg-dark-border-subtle" />

      <InfoFieldRow label="Safety actions">
        <View className="gap-1">
          <SafetyAction Icon={Trash2} label="Clear chat" onPress={props.onClearChat} />
          {/* Block/Report are peer actions — only meaningful in a 1:1. */}
          {!props.isGroup ? (
            <>
              <SafetyAction Icon={Ban} label="Block user" onPress={props.onBlockUser} />
              <SafetyAction Icon={Flag} label="Report user" onPress={props.onReportUser} />
            </>
          ) : null}
          {props.onLeaveOrDelete !== undefined ? (
            <SafetyAction
              Icon={props.isGroup ? LogOut : Trash2}
              label={props.isGroup ? 'Leave group' : 'Delete chat'}
              onPress={props.onLeaveOrDelete}
            />
          ) : null}
        </View>
      </InfoFieldRow>
    </ScrollView>
  );
}

// ── Members tab ──────────────────────────────────────────────────────────

function MembersTab(props: ChatInfoModalProps): JSX.Element {
  const { members, canManageMembers, onRemoveMembers, onAddMembers } = props;
  const leftLock = props.isGroup && props.hasLeftGroup === true;
  const list = members ?? [];
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const toggle = (id: string): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const startSelecting = (id: string): void => {
    setSelecting(true);
    setSelected(new Set([id]));
  };
  const exitSelecting = (): void => {
    setSelecting(false);
    setSelected(new Set());
  };

  return (
    <View className="flex-1">
      <ScrollView contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 16 }}>
        {canManageMembers === true && onAddMembers !== undefined && !selecting ? (
          <Gated locked={leftLock}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Add member"
              disabled={leftLock}
              onPress={onAddMembers}
              className="h-11 flex-row items-center gap-3 active:opacity-80"
            >
              <View className="h-9 w-9 items-center justify-center rounded-full bg-light-surface-elevated dark:bg-dark-surface-elevated">
                <UserPlus size={18} color={ACCENT} />
              </View>
              <Text className="text-[16px] font-semibold text-light-accent-brand dark:text-dark-accent-brand">
                Add member
              </Text>
            </Pressable>
          </Gated>
        ) : null}
        {list.map((m) => (
          <MemberRow
            key={m.id}
            member={m}
            {...props}
            selecting={selecting}
            selected={selected.has(m.id)}
            onToggleSelect={() => toggle(m.id)}
            onStartSelect={() => startSelecting(m.id)}
          />
        ))}
      </ScrollView>

      {selecting ? (
        <View className="h-[52px] flex-row items-center justify-between border-t border-light-border-subtle dark:border-dark-border-subtle px-4">
          <Pressable onPress={exitSelecting} accessibilityRole="button">
            <Text className="text-[14px] text-light-text-primary dark:text-dark-text-primary">
              {selected.size} selected
            </Text>
          </Pressable>
          {canManageMembers === true ? (
            <Gated locked={leftLock}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Remove from group"
                disabled={leftLock || selected.size === 0}
                onPress={() => {
                  onRemoveMembers?.(Array.from(selected));
                  exitSelecting();
                }}
                className="flex-row items-center gap-1 active:opacity-70"
              >
                <Trash2 size={16} color={ICON_DANGER} />
                <Text className="text-[14px] font-semibold text-[#FF5353]">Remove from group</Text>
              </Pressable>
            </Gated>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

function MemberRow(
  props: ChatInfoModalProps & {
    member: ChatInfoMember;
    compact?: boolean;
    selecting?: boolean;
    selected?: boolean;
    onToggleSelect?: () => void;
    onStartSelect?: () => void;
  },
): JSX.Element {
  const {
    member,
    compact,
    selecting,
    selected,
    onToggleSelect,
    onStartSelect,
    canManageMembers,
    onViewMemberProfile,
    onAddMemberContact,
    onMakeAdmin,
    onRemoveMembers,
  } = props;
  const [menuOpen, setMenuOpen] = useState(false);
  const kebabRef = useRef<unknown>(null);

  return (
    <View className="relative">
      <Pressable
        onPress={selecting === true ? onToggleSelect : undefined}
        className="h-11 flex-row items-center gap-2 active:opacity-80"
      >
        {selecting === true ? (
          <View
            className={`h-5 w-5 items-center justify-center rounded border ${
              selected === true
                ? 'bg-light-accent-brand dark:bg-dark-accent-brand border-transparent'
                : 'border-light-border-subtle dark:border-dark-border-subtle'
            }`}
          >
            {selected === true ? <Text className="text-[12px] text-white">✓</Text> : null}
          </View>
        ) : null}
        <ProfileAvatar name={member.name} uri={member.avatarUri} sizeClassName="w-9 h-9 rounded-full" />
        <View className="flex-1 min-w-0">
          <Text
            className="text-[16px] font-semibold text-light-text-primary dark:text-dark-text-primary"
            numberOfLines={1}
          >
            {member.name}
          </Text>
          {member.subtitle !== undefined && member.subtitle.length > 0 ? (
            <Text
              className="text-[13px] text-light-text-tertiary dark:text-dark-text-tertiary"
              numberOfLines={1}
            >
              {member.subtitle}
            </Text>
          ) : null}
        </View>
        {member.role !== undefined && member.role.length > 0 ? (
          <Text className="text-[12px] text-light-text-secondary dark:text-dark-text-secondary">
            {member.role}
          </Text>
        ) : null}
        {compact !== true && selecting !== true ? (
          <Pressable
            ref={kebabRef as never}
            accessibilityRole="button"
            accessibilityLabel={`Options for ${member.name}`}
            hitSlop={6}
            onPress={() => setMenuOpen((o) => !o)}
            className="h-5 w-5 items-center justify-center active:opacity-60"
          >
            <Text className="text-[18px] leading-[18px] text-light-text-tertiary dark:text-dark-text-tertiary">
              ⋮
            </Text>
          </Pressable>
        ) : null}
      </Pressable>

      {/* Per-member options — AnchoredPopover (placement below, right-aligned)
          replaces the hard-coded absolute offset + manual dismiss scrim. */}
      <AnchoredPopover
        anchorRef={kebabRef}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        placement="bottom"
        align="end"
      >
        <MemberKebabMenu
          isAdminViewer={
            canManageMembers === true && !(props.isGroup && props.hasLeftGroup === true)
          }
          onViewProfile={() => {
            setMenuOpen(false);
            onViewMemberProfile?.(member.id);
          }}
          onAddContact={() => {
            setMenuOpen(false);
            onAddMemberContact?.(member.id);
          }}
          onSelect={() => {
            setMenuOpen(false);
            onStartSelect?.();
          }}
          onMakeAdmin={() => {
            setMenuOpen(false);
            onMakeAdmin?.(member.id);
          }}
          onRemove={() => {
            setMenuOpen(false);
            onRemoveMembers?.([member.id]);
          }}
        />
      </AnchoredPopover>
    </View>
  );
}

function KebabItem({
  Icon,
  label,
  danger,
  onPress,
}: {
  Icon: typeof Info;
  label: string;
  danger?: boolean;
  onPress: () => void;
}): JSX.Element {
  return (
    <Pressable
      accessibilityRole="menuitem"
      accessibilityLabel={label}
      onPress={onPress}
      className="h-7 flex-row items-center gap-3 rounded-lg px-3 active:bg-light-surface-elevated dark:active:bg-dark-surface-elevated"
    >
      <Icon size={16} color={danger === true ? ICON_DANGER : ICON_ACTIVE} />
      <Text
        className={`text-[14px] ${
          danger === true ? 'text-[#FF5353]' : 'text-light-text-secondary dark:text-dark-text-secondary'
        }`}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function MemberKebabMenu({
  isAdminViewer,
  onViewProfile,
  onAddContact,
  onSelect,
  onMakeAdmin,
  onRemove,
}: {
  isAdminViewer: boolean;
  onViewProfile: () => void;
  onAddContact: () => void;
  onSelect: () => void;
  onMakeAdmin: () => void;
  onRemove: () => void;
}): JSX.Element {
  return (
    <View className="w-[253px] rounded-xl border border-light-border-subtle dark:border-dark-border-subtle bg-light-surface-panel dark:bg-dark-surface-panel p-1">
      <KebabItem Icon={UserRound} label="View profile" onPress={onViewProfile} />
      <KebabItem Icon={UserPlus} label="Add contact" onPress={onAddContact} />
      {isAdminViewer ? (
        <>
          <View className="my-1 h-px bg-light-border-subtle dark:bg-dark-border-subtle" />
          <KebabItem Icon={ChevronRight} label="Select" onPress={onSelect} />
          <KebabItem Icon={Users} label="Make admin" onPress={onMakeAdmin} />
          <View className="my-1 h-px bg-light-border-subtle dark:bg-dark-border-subtle" />
          <KebabItem Icon={Trash2} label="Remove from group" danger onPress={onRemove} />
        </>
      ) : null}
    </View>
  );
}
